import { computed, effect, signal, Service } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

/** Light / dark theme for the console (okto-style toggle). */
@Service()
export class ThemeService {
  private readonly modeSignal = signal<ThemeMode>(ThemeService.readStored());

  /** Active theme mode. */
  readonly mode = this.modeSignal.asReadonly();
  /** True when dark mode is active. */
  readonly isDark = computed(() => this.modeSignal() === 'dark');

  constructor() {
    effect(() => {
      this.apply(this.modeSignal());
    });
  }

  /** Switches between light and dark. */
  toggle(): void {
    this.modeSignal.update((mode) => (mode === 'dark' ? 'light' : 'dark'));
  }

  /** Sets an explicit theme mode. */
  setMode(mode: ThemeMode): void {
    this.modeSignal.set(mode);
  }

  private apply(mode: ThemeMode): void {
    const root = document.documentElement;

    root.classList.toggle('dark', mode === 'dark');
    root.style.colorScheme = mode;
    localStorage.setItem('theme', mode);

    const background = getComputedStyle(root)
      .getPropertyValue('--ui-color-background')
      .trim();
    if (background) {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', background);
    }
  }

  private static readStored(): ThemeMode {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('theme') : null;
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }
}
